import { maxBy } from 'lodash-es';

import {
  deriveRefills,
  RX_VALUE_DECIMAL_PLACES,
  RxMetricKey,
  RxPeriodMode,
  RxProductSeries,
} from './rx-trends.types';

/** Never a real product name or GUID, so it cannot collide with a product id. */
export const RX_ALL_PRODUCTS_ID = '__rx_all_products__';

/** Series key for the summed line, kept distinct from every product id. */
export const RX_TOTAL_SERIES_KEY = '__rx_total__';

const VALUE_SCALE = Math.pow(10, RX_VALUE_DECIMAL_PLACES);

/** One product's values for the selected metric and period mode. */
export interface RxProductLine {
  productId: string;
  productName: string;
  isCompetitor: boolean;
  values: (number | null)[];
}

/**
 * What the All pill charts: one line per product, plus the total across our own
 * products.
 *
 * `total` is null when fewer than two of our products contribute, since a total
 * over a single product is that product's own line drawn twice.
 */
export interface RxAllProductsView {
  categories: string[];
  productLines: RxProductLine[];
  total: (number | null)[] | null;
}

/** The two fields display order depends on, whatever shape carries them. */
export interface RxProductOrderKey {
  name: string;
  isCompetitor: boolean;
}

/**
 * Display order for anything product-keyed — the pill row, the All view's lines,
 * its legend chips and its table columns — so all four read in the same order.
 *
 * Ours before a competitor's, alphabetical within each group. `localeCompare`
 * rather than `<`, so accented and non-Latin product names sort by the user's
 * locale instead of by code point.
 */
export function compareRxProductsForDisplay(
  a: RxProductOrderKey,
  b: RxProductOrderKey,
): number {
  if (a.isCompetitor !== b.isCompetitor) return a.isCompetitor ? 1 : -1;
  return a.name.localeCompare(b.name);
}

export const normalizeProductName = (name: string): string => (name ?? '').trim().toLowerCase();

/**
 * Which series a pill charts: the summed view, one product's series, or nothing
 * when the pill is a ladder-only product the Rx payload never mentioned.
 *
 * Matched on the Dynamics product id where the payload carries one, on name
 * where it does not — it is populated on only some rows today. Drop the name
 * leg once every row has an id.
 */
export function resolveRxSeriesKey(
  product: { id: string; name: string } | undefined,
  series: RxProductSeries[],
): string | null {
  if (!product) return null;
  if (product.id === RX_ALL_PRODUCTS_ID) return RX_ALL_PRODUCTS_ID;

  const matchById = series.find(entry => entry.productId === product.id);
  if (matchById) return matchById.productId;

  const wanted = normalizeProductName(product.name);
  const matchByName = series.find(entry => normalizeProductName(entry.productName) === wanted);
  return matchByName?.productId ?? null;
}

/**
 * Builds the All-products view for one metric.
 *
 * Competitor products are charted alongside ours but deliberately excluded from
 * the total: mixing their prescriptions into it would make the figure unusable
 * as a read on our own performance.
 */
export function buildAllProductsView(
  series: RxProductSeries[],
  mode: RxPeriodMode,
  metric: RxMetricKey,
): RxAllProductsView | null {
  if (!series?.length) return null;

  const labelSource = pickLongestLabelSource(series, mode);
  const categories = (mode === 'weekly' ? labelSource.weeklyLabels : labelSource.monthlyLabels) ?? [];
  const bucketCount = Math.max(categories.length, maxStoredBucketCount(series, mode));

  const productLines: RxProductLine[] = series
    .map((product) => ({
      productId: product.productId,
      productName: product.productName,
      isCompetitor: !!product.isCompetitor,
      values: buildMetricValues(product, mode, metric, bucketCount),
    }))
    .sort((a, b) =>
      compareRxProductsForDisplay(
        { name: a.productName, isCompetitor: a.isCompetitor },
        { name: b.productName, isCompetitor: b.isCompetitor },
      ),
    );

  const ownProductLines = productLines.filter((line) => !line.isCompetitor);

  return {
    categories,
    productLines,
    total: ownProductLines.length > 1 ? sumLines(ownProductLines, bucketCount) : null,
  };
}

/**
 * One product's values for a metric, padded to `bucketCount`.
 *
 * Refills is not stored — the source sends NRx and TRx — so it is derived per
 * bucket before anything downstream sums or plots it. A bucket missing either
 * operand stays a gap rather than reporting the whole of TRx as refills.
 */
export function buildMetricValues(
  product: RxProductSeries,
  mode: RxPeriodMode,
  metric: RxMetricKey,
  bucketCount: number,
): (number | null)[] {
  const stored = product[mode];
  const values: (number | null)[] = new Array(bucketCount);

  for (let bucket = 0; bucket < bucketCount; bucket++) {
    if (metric === 'Refills') {
      values[bucket] = deriveRefills(stored?.TRx?.[bucket] ?? null, stored?.NRx?.[bucket] ?? null);
    } else {
      values[bucket] = stored?.[metric]?.[bucket] ?? null;
    }
  }

  return values;
}

/**
 * A missing value is skipped, not counted as zero: the wire contract treats an
 * absent row as unknown, so the bucket understates rather than showing a gap,
 * which a rep would read as "no scripts". Null only when every product is
 * unknown at that bucket.
 */
function sumLines(lines: RxProductLine[], bucketCount: number): (number | null)[] {
  const summed: (number | null)[] = new Array(bucketCount);

  for (let bucket = 0; bucket < bucketCount; bucket++) {
    let total = 0;
    let hasKnownValue = false;

    for (const line of lines) {
      const value = line.values[bucket];
      if (value === null || value === undefined) continue;
      total += value;
      hasKnownValue = true;
    }

    summed[bucket] = hasKnownValue ? Math.round(total * VALUE_SCALE) / VALUE_SCALE : null;
  }

  return summed;
}

function pickLongestLabelSource(series: RxProductSeries[], mode: RxPeriodMode): RxProductSeries {
  return maxBy(
    series,
    (product) => (mode === 'weekly' ? product.weeklyLabels : product.monthlyLabels)?.length ?? 0,
  );
}

/**
 * Buckets actually present in the stored values, regardless of labels.
 *
 * Labels come from the period anchors, values do not, so a series can hold a
 * full set of values with no labels to hang them on — an incremental sync that
 * arrives without periods does exactly that. Charting `bucketCount` off the
 * labels alone would then plot nothing; taking the larger of the two keeps the
 * figures on screen and costs only the x-axis labels.
 */
export function maxStoredBucketCount(series: RxProductSeries[], mode: RxPeriodMode): number {
  if (!series?.length) return 0;
  return Math.max(
    ...series.map((product) =>
      Math.max(product[mode]?.NRx?.length ?? 0, product[mode]?.TRx?.length ?? 0),
    ),
  );
}
